import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { usePageMeta, eur, shortDate } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronLeft, Mail, TrendingUp, Package } from "lucide-react";

export default function WeeklyDigest() {
  const { user } = useAuth();
  const t = useT();
  usePageMeta(t("wdig.meta_title"));
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try { const { data: d } = await api.get("/coach/weekly-digest/preview"); setData(d); }
    catch { setData({ count: 0, gross_cents: 0, offerings: [], trend_4w: [] }); }
  }, []);
  useEffect(() => { if (user?.role === "coach") load(); }, [user, load]);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  const maxGross = Math.max(1, ...((data?.trend_4w || []).map((w) => w.gross_cents || 0)));

  return (
    <div data-testid="coach-weekly-digest-page">
      <Seo title={t("wdig.seo")} noindex />
      <Link to="/app/coach/settings" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="wdig-back">
        <ChevronLeft className="h-4 w-4" /> {t("cset.title")}
      </Link>
      <PageHeader eyebrow={t("wdig.eyebrow")} title={t("wdig.title")} subtitle={t("wdig.subtitle")} />

      {data === null ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
      ) : (
        <div className="space-y-5">
          {/* This week */}
          <div className="rounded-2xl border border-border bg-card p-5" data-testid="wdig-thisweek">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Mail className="h-4 w-4 text-primary" /> {t("wdig.this_week")}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <p className="font-display text-3xl text-foreground">{data.count ?? 0}</p>
                <p className="text-xs text-muted-foreground">{t("wdig.bookings")}</p>
              </div>
              <div>
                <p className="font-display text-3xl text-primary">{eur(data.gross_cents || 0)}</p>
                <p className="text-xs text-muted-foreground">{t("wdig.gross")}</p>
              </div>
            </div>
            {data.week_start && <p className="mt-3 text-xs text-muted-foreground">{shortDate(data.week_start)} – {shortDate(data.week_end)}</p>}
          </div>

          {/* 4-week trend */}
          {(data.trend_4w || []).length > 0 && (
            <div className="rounded-2xl border border-border bg-card p-5" data-testid="wdig-trend">
              <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <TrendingUp className="h-4 w-4 text-primary" /> {t("wdig.trend")}
              </div>
              <div className="flex items-end justify-between gap-3" style={{ height: 120 }}>
                {data.trend_4w.map((w, i) => (
                  <div key={i} className="flex flex-1 flex-col items-center gap-1.5">
                    <div className="flex w-full flex-1 items-end">
                      <div className="w-full rounded-t-md bg-primary/80" style={{ height: `${Math.max(4, Math.round((w.gross_cents / maxGross) * 96))}px` }} title={eur(w.gross_cents || 0)} />
                    </div>
                    <span className="text-[10px] font-medium text-foreground">{eur(w.gross_cents || 0)}</span>
                    <span className="text-[10px] text-muted-foreground">{shortDate(w.week_start)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Offerings breakdown */}
          <div>
            <p className="mb-2 flex items-center gap-1.5 font-display text-lg text-foreground"><Package className="h-4 w-4 text-primary" /> {t("wdig.offerings")}</p>
            {(data.offerings || []).length === 0 ? (
              <p className="rounded-2xl border border-dashed border-border bg-card p-6 text-center text-sm text-muted-foreground" data-testid="wdig-offerings-empty">{t("wdig.offerings_empty")}</p>
            ) : (
              <div className="space-y-2">
                {data.offerings.map((o, i) => (
                  <div key={i} data-testid={`wdig-offering-${i}`} className="rounded-2xl border border-border bg-card p-4">
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate font-semibold text-foreground">{o.title || "—"}</p>
                      <span className="shrink-0 text-sm font-medium text-primary">{eur(o.gross_cents || 0)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{t("wdig.n_bookings", { n: o.count || 0 })}</p>
                    {(o.blocks || []).length > 0 && (
                      <div className="mt-2 space-y-1 border-t border-border pt-2">
                        {o.blocks.map((b, j) => (
                          <div key={j} className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground">{b.title}</span>
                            <span className="text-foreground">{eur(b.gross_cents || 0)} · {b.count}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {data.last_sent_at && (
            <p className="text-center text-xs text-muted-foreground" data-testid="wdig-last-sent">
              {t("wdig.last_sent", { when: shortDate(data.last_sent_at), n: data.last_sent_count || 0, gross: eur(data.last_sent_gross_cents || 0) })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
